/**
 * The league view's week picker on a league whose season runs in weeks
 * (ISSUE-950 R6, Scores UX spec v1.0 section 4.2): the same fixed 64px strip
 * as `DateStrip`, one chip per week that has games.
 *
 * Each chip is 56px tall and at least 72 wide (`weekChipMinWidth`), centered
 * in the row so it never stretches: "Wk 7" over its range "Oct 16-20" (an
 * ASCII hyphen). The repeated "Week" caption is gone (fixes web L4). The full
 * sentence, "Week 7, Friday, Oct 16 to Tuesday, Oct 20", is the accessible
 * name. Selected is a `primarySolid` fill with `onPrimary` text.
 *
 * NOT a tablist, for the same reason as `DateStrip` (ISSUE-957). A sibling of
 * it, not a mode inside it: the two take different data.
 */
import { weekChipLabel } from "@sportsmet/theme/scores";
import { cn } from "@sportsmet/ui";
import { type ReactNode, useEffect, useRef } from "react";

import {
  STRIP_SCROLLER_CLASS,
  stripChipClassName,
  stripChipLabel,
} from "./DateStrip";
import { WEEK_STRIP_LABEL } from "./leagueSelectorContract";
import { type typeSeasonWeek, weekRangeLabel } from "./seasonWeek";
import { scrollStripToChip } from "./stripScroll";

export type typeWeekStripProps = {
  /** Only weeks that have games. Sorted by week number internally, so the
   *  strip does not depend on the read's order. */
  weeks: readonly typeSeasonWeek[];
  selectedWeek: number | null;
  onSelectWeek: (seasonWeek: number) => void;
};

export const WeekStrip = (props: typeWeekStripProps): ReactNode => {
  const stripRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const renderedWeeks = [...props.weeks].sort(
    (a, b) => a.seasonWeek - b.seasonWeek,
  );
  const weekKey = renderedWeeks.map((week) => week.seasonWeek).join(",");
  useEffect(() => {
    const selected = props.selectedWeek;
    const strip = stripRef.current;
    if (selected === null || strip === null) return;
    const chip = [...strip.children].find(
      (child) => child.getAttribute("data-week") === String(selected),
    );
    if (!(chip instanceof HTMLElement)) return;
    scrollStripToChip({ strip, chip, isInitial: !hasScrolledRef.current });
    hasScrolledRef.current = true;
  }, [props.selectedWeek, weekKey]);

  if (renderedWeeks.length === 0) {
    return null;
  }

  return (
    <div
      ref={stripRef}
      role="group"
      aria-label={WEEK_STRIP_LABEL}
      className={STRIP_SCROLLER_CLASS}
      data-testid="week-strip"
    >
      {renderedWeeks.map((week) => {
        const isSelected = week.seasonWeek === props.selectedWeek;
        const label = weekChipLabel(week);
        return (
          <button
            key={week.seasonWeek}
            type="button"
            className={cn(
              "min-w-week-chip-min-width px-2",
              stripChipClassName(isSelected),
            )}
            data-testid="week-strip-week"
            data-week={String(week.seasonWeek)}
            aria-current={isSelected ? "true" : undefined}
            aria-label={stripChipLabel({
              label: weekRangeLabel(week),
              isToday: false,
              isSelected,
            })}
            onClick={() => {
              props.onSelectWeek(week.seasonWeek);
            }}
          >
            <span className="text-body-strong" data-testid="week-strip-marker">
              {label.week}
            </span>
            <span
              className={cn(
                "whitespace-nowrap text-small",
                isSelected ? "text-on-primary" : "text-ink-secondary",
              )}
              data-testid="week-strip-range"
            >
              {label.range}
            </span>
          </button>
        );
      })}
    </div>
  );
};
